import type { PosMoneyGoodsLine, PosMoneyInput, PosServiceLineInput, ShippingFeePayer } from '@tcr-erp/shared';
import type { OrderItem } from '../types';
import type { ServiceOption } from '../components/ServiceFeeRow';
import { buildPosServiceLine } from './buildPosServiceLine';
import { computePosTotals } from './orderItemCalc';

export interface PosMoneyShippingParams {
  mode: 'self' | 'carrier';
  fee: number;
  feePayer: ShippingFeePayer;
  prepaidAmount: number;
  debtAmount: number;
  /** Chỉ có nghĩa khi `mode === 'self'` — dịch vụ cho dòng phí VC. */
  serviceId: string | null;
  /** Khoản giảm phí VC do CTKM freeship. Pipeline tự kẹp về [0, phí khách trả]. */
  discount?: number;
}

export interface BuildPosMoneyInputParams {
  /**
   * TCR-1365 — cách khách nhận hàng, đọc từ field `receive_method` của form.
   *
   * CẦN ở đây vì `shipping: null` KHÔNG phân biệt được "lấy tại chi nhánh"
   * (được dùng điểm) với "gửi kho"/đơn dựng thiếu dữ liệu (không được). Để
   * BẮT BUỘC (không optional, không mặc định) là cố ý: mọi nơi dựng input
   * tiền phải khai rõ, `tsc` bắt hộ call site sót thay vì âm thầm rơi về một
   * mặc định nào đó rồi tính lệch tiền so với BE.
   */
  receiveMethod: 'delivery' | 'pickup' | 'vault';
  items: OrderItem[];
  surcharge: number;
  surchargeServiceId: string | null;
  voucherDiscount: number;
  tradeDiscount: number;
  loyaltyRedeemValue: number;
  /** Đơn B2B loại khỏi điểm tích luỹ ở mọi nơi trong hệ thống. */
  isB2B: boolean;
  /** `null` ⇒ đơn bán tại quầy. */
  shipping: PosMoneyShippingParams | null;
  serviceOptions: { surcharge: ServiceOption[]; shipping: ServiceOption[] };
  defaultVatRates: { surcharge: number; shipping: number };
}

/**
 * Dịch giỏ hàng + form POS thành `PosMoneyInput` cho `computePosMoney`.
 *
 * Mirror `buildPosMoneyInput` phía BE (`pos-order.service.ts` dùng chính hàm
 * đó khi ghi đơn thật) — cùng quy tắc:
 *  - Mọi ca KHÁCH trả phí ⇒ KHÔNG sinh dòng: phí đi thẳng vào `shipping.fee`,
 *    được cộng vào số phải thu ở bước 7, không qua nhóm thuế suất (ADR-029 D1).
 *  - CHỈ `mode === 'self'` + `feePayer === 'seller'` mới sinh dòng, `taxable:
 *    false` — snapshot số tiền shop tự bỏ ra, để trừ vào doanh thu.
 *
 * `POSPage` gọi hàm này ở HAI nơi: `useMemo` hiển thị (đọc watched state) và
 * `handleCheckout` (đọc `values` vừa revalidate) — cùng một hàm cho cả hai
 * chỗ để không lặp lại logic dựng input, đúng nguyên tắc "một nguồn duy nhất"
 * của Task 18.
 */
export function buildPosMoneyInput(params: BuildPosMoneyInputParams): PosMoneyInput {
  const { totalGoods, totalDiscount } = computePosTotals(params.items);
  const goodsLines: PosMoneyGoodsLine[] = params.items.map((item) => ({
    id: item.id,
    amount: item.totalAmount,
    vatRate: item.vatOverride ?? item.vat ?? 0,
  }));

  const serviceLines: PosServiceLineInput[] = [];
  const surchargeLine = buildPosServiceLine(
    'surcharge',
    params.surchargeServiceId,
    params.surcharge,
    true,
    params.serviceOptions.surcharge,
    params.defaultVatRates.surcharge,
  );
  if (surchargeLine) serviceLines.push(surchargeLine);

  if (params.shipping && params.shipping.mode === 'self') {
    const shippingLine = buildPosServiceLine(
      'shipping',
      params.shipping.serviceId,
      params.shipping.fee,
      params.shipping.feePayer === 'buyer',
      params.serviceOptions.shipping,
      params.defaultVatRates.shipping,
    );
    // ADR-029 D1 — phí KHÁCH trả KHÔNG sinh dòng dịch vụ nữa: `computePosMoney` bước 7 cộng thẳng
    // khoản đó vào số phải thu, nên khách trả đúng số thu ngân nhập ở cả hai cách giao. Còn sinh
    // dòng chịu thuế là cộng phí HAI LẦN.
    // Phải khớp TỪNG DÒNG với `build-pos-money-input.ts` phía BE — hai bên lệch là màn hình hiện
    // một số còn hệ thống thu một số khác.
    if (shippingLine && !shippingLine.taxable) {
      serviceLines.push(shippingLine);
    }
  }

  const hasShipping = !!params.shipping;
  // TCR-1365 — mirror `loyaltyEligible` phía BE (`pos-order-payment.service.ts`).
  // Hai bên lệch nhau là màn hình hiện một số còn hệ thống thu một số khác —
  // và với đơn tại quầy thanh toán tiền mặt thì lệch đó dính thẳng guard
  // `CASH_AMOUNT_MISMATCH` (BE từ chối 400).
  const loyaltyEligible =
    !params.isB2B && (hasShipping || params.receiveMethod === 'pickup');

  return {
    totalGoods,
    totalLineDiscount: totalDiscount,
    goodsLines,
    orderDiscounts: {
      voucher: params.voucherDiscount,
      trade: params.isB2B ? params.tradeDiscount : 0,
      // TCR-1365 (thay Residual R1 cũ): điểm tích luỹ trừ cho đơn GIAO HÀNG và
      // đơn LẤY TẠI CHI NHÁNH. Đơn `vault` và khách B2B vẫn bị loại.
      loyalty: loyaltyEligible ? params.loyaltyRedeemValue : 0,
    },
    serviceLines,
    shipping: params.shipping
      ? {
          mode: params.shipping.mode,
          fee: params.shipping.fee,
          feePayer: params.shipping.feePayer,
          prepaidAmount: params.shipping.prepaidAmount,
          debtAmount: params.shipping.debtAmount,
          discount: params.shipping.discount ?? 0,
        }
      : undefined,
  };
}
